import * as XLSX from 'xlsx'

/**
 * Exportiert Techniker-Wissen in eine formatierte Excel-Datei
 * @param {Array} knowledgeItems - Array von Wissens-Einträgen
 * @param {Object} options - Export-Optionen
 */
export const exportKnowledgeToExcel = (knowledgeItems, options = {}) => {
  const {
    filename = `Techniker-Wissen_${new Date().toISOString().split('T')[0]}.xlsx`,
    groupByDevice = true
  } = options

  // Helper: Kategorie-Label
  const getCategoryLabel = category => {
    switch (category) {
      case 'error_code':
        return 'Fehlercode'
      case 'troubleshooting':
        return 'Troubleshooting'
      case 'maintenance':
        return 'Wartung'
      case 'workaround':
        return 'Workaround'
      case 'general_knowledge':
        return 'Allgemeines Wissen'
      default:
        return category || '-'
    }
  }

  // Helper: Schwierigkeitsgrad-Label
  const getDifficultyLabel = difficulty => {
    switch (difficulty) {
      case 'easy':
        return 'Einfach'
      case 'medium':
        return 'Mittel'
      case 'hard':
        return 'Schwierig'
      default:
        return difficulty || '-'
    }
  }

  // Helper: Datum formatieren
  const formatDate = timestamp => {
    if (!timestamp) return '-'
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return '-'
    }
  }

  // Konvertiere Daten in Excel-Format
  const prepareDataForExcel = items => {
    return items.map(item => ({
      Gerät: item.deviceName || '-',
      Titel: item.problemTitle || '-',
      Kategorie: getCategoryLabel(item.category),
      Fehlercode: item.errorCode || '-',
      Beschreibung: item.problemDescription || '-',
      Lösung: item.solution || '-',
      Schwierigkeit: getDifficultyLabel(item.difficulty),
      'Geschätzte Zeit': item.estimatedTime || '-',
      'Eingereicht von': item.submittedBy || '-',
      'Moderiert am': formatDate(item.moderatedAt)
    }))
  }

  // Erstelle Workbook
  const wb = XLSX.utils.book_new()

  if (groupByDevice) {
    // Gruppiere nach Gerät
    const deviceGroups = {}
    knowledgeItems.forEach(item => {
      const device = item.deviceName || 'Unbekannt'
      if (!deviceGroups[device]) {
        deviceGroups[device] = []
      }
      deviceGroups[device].push(item)
    })

    console.log('📊 Excel Export - Gefundene Geräte:', Object.keys(deviceGroups))
    console.log('📊 Excel Export - Anzahl Einträge pro Gerät:', Object.keys(deviceGroups).map(device => `${device}: ${deviceGroups[device].length}`))

    // Erstelle Sheet pro Gerät
    Object.keys(deviceGroups)
      .sort()
      .forEach(deviceName => {
        const deviceItems = prepareDataForExcel(deviceGroups[deviceName])
        const ws = XLSX.utils.json_to_sheet(deviceItems)

        // Spaltenbreiten setzen
        ws['!cols'] = [
          { wch: 15 }, // Gerät
          { wch: 30 }, // Titel
          { wch: 15 }, // Kategorie
          { wch: 12 }, // Fehlercode
          { wch: 40 }, // Beschreibung
          { wch: 40 }, // Lösung
          { wch: 12 }, // Schwierigkeit
          { wch: 15 }, // Geschätzte Zeit
          { wch: 20 }, // Eingereicht von
          { wch: 18 } // Moderiert am
        ]

        // Sheet-Name begrenzen (Excel max 31 Zeichen)
        const sheetName = deviceName.substring(0, 31)
        console.log(`📄 Erstelle Sheet: "${sheetName}" mit ${deviceItems.length} Einträgen`)
        XLSX.utils.book_append_sheet(wb, ws, sheetName)
      })
  } else {
    // Einzelnes Sheet mit allen Daten
    const allData = prepareDataForExcel(knowledgeItems)
    const ws = XLSX.utils.json_to_sheet(allData)

    // Spaltenbreiten setzen
    ws['!cols'] = [
      { wch: 15 }, // Gerät
      { wch: 30 }, // Titel
      { wch: 15 }, // Kategorie
      { wch: 12 }, // Fehlercode
      { wch: 40 }, // Beschreibung
      { wch: 40 }, // Lösung
      { wch: 12 }, // Schwierigkeit
      { wch: 15 }, // Geschätzte Zeit
      { wch: 20 }, // Eingereicht von
      { wch: 18 } // Moderiert am
    ]

    XLSX.utils.book_append_sheet(wb, ws, 'Techniker-Wissen')
  }

  // Excel-Datei generieren und download
  XLSX.writeFile(wb, filename)
}

/**
 * Lädt alle gefilterten Wissensdaten aus Firestore
 * @param {Function} buildBaseQuery - Query-Builder-Funktion
 * @param {string} companyId - Company ID
 * @param {string} debouncedSearch - Suchbegriff
 * @returns {Promise<Array>} Array aller Wissens-Einträge
 */
export const loadAllKnowledgeForExport = async (
  buildBaseQuery,
  companyId,
  debouncedSearch = ''
) => {
  const { getDocs } = await import('firebase/firestore')

  try {
    const q = await buildBaseQuery(companyId)
    const snapshot = await getDocs(q)

    let items = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    console.log(`📥 Geladene Items aus Firestore: ${items.length}`)

    // Debug: Zeige alle verschiedenen Geräte
    const uniqueDevices = [...new Set(items.map(item => item.deviceName || 'Unbekannt'))]
    console.log(`📱 Gefundene Geräte in Daten: ${uniqueDevices.join(', ')}`)

    // Client-side Search-Filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase()
      const beforeFilter = items.length
      items = items.filter(
        item =>
          item.problemTitle?.toLowerCase().includes(searchLower) ||
          item.solution?.toLowerCase().includes(searchLower) ||
          item.problemDescription?.toLowerCase().includes(searchLower) ||
          item.errorCode?.toLowerCase().includes(searchLower)
      )
      console.log(`🔍 Nach Suchfilter "${debouncedSearch}": ${items.length} (vorher: ${beforeFilter})`)
    }

    return items
  } catch (error) {
    console.error('Error loading all knowledge for export:', error)
    throw error
  }
}
